import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import ClearFindInViewFilter from '@/components/ui/find-in-view-filter/clear-find-in-view-filter';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import {
  PDKBarcodeIcon,
  PDKInboxIcon,
  PDKMessageSquareXIcon,
} from '@/components/ui/pdk-icons';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import type { Notification } from '@/models/registry';
import { observer } from 'mobx-react-lite';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { InboxLineItem } from './inbox-line-item';

const VirtualizedInboxList = observer(
  ({
    notifications,
    getContainerId,
    calcIsCurrentDeal,
    markAsRead,
    markAsUnread,
    onDeleteNotification,
    numOfFilteredNotifications,
  }: {
    notifications: Notification[];
    getContainerId: (dealId: string) => string;
    calcIsCurrentDeal: (dealShortId: string) => boolean;
    markAsRead: (notification: Notification) => void;
    markAsUnread: (notification: Notification) => void;
    onDeleteNotification: (notification: Notification) => void;
    numOfFilteredNotifications: number;
  }) => {
    const Row = ({
      index,
      style,
    }: {
      index: number;
      style: React.CSSProperties;
    }) => {
      const notification = notifications[index];

      if (index === notifications.length) {
        return (
          <div style={style}>
            <ClearFindInViewFilter
              message={
                <div className="text-xs text-gray-500 whitespace-nowrap">
                  <span className="text-black">
                    {`${numOfFilteredNotifications} notifications`}{' '}
                  </span>
                  hidden by filters
                </div>
              }
            />
          </div>
        );
      }

      return (
        <div
          style={style}
          key={notification.id}
          className="scroll-m-18"
          id={getContainerId(notification.deal_id)}
        >
          <ContextMenu>
            <ContextMenuTrigger className="ContextMenuTrigger">
              <InboxLineItem
                notification={notification}
                isActive={calcIsCurrentDeal(notification.deal?.short_id ?? '')}
                markAsRead={markAsRead}
              />
            </ContextMenuTrigger>
            <ContextMenuContent className="bg-white w-64 border rounded-md p-2 z-20">
              <ContextMenuItem
                className="bg-white flex flex-row gap-2 outline-none cursor-pointer hover:bg-gray-100 p-2 justify-between"
                onSelect={() => {
                  if (notification.isRead) {
                    markAsUnread(notification);
                  } else {
                    markAsRead(notification);
                  }
                }}
              >
                <div className="flex flex-row gap-2">
                  <PDKInboxIcon className="size-4" />
                  {notification.isRead ? (
                    <div>Mark as unread</div>
                  ) : (
                    <div>Mark as read</div>
                  )}
                </div>
                <div>
                  <KeyboardKeyIcon>
                    {HOTKEYS.TOGGLE_READ_UNREAD}
                  </KeyboardKeyIcon>
                </div>
              </ContextMenuItem>
              <ContextMenuItem
                onSelect={() => {
                  if (!notification.id) {
                    toast({
                      title: 'Sorry no notification id found',
                    });
                    return;
                  }

                  navigator.clipboard.writeText(notification.id);

                  toast({
                    title: 'Notification ID copied to clipboard',
                  });
                }}
              >
                <div className="flex flex-row gap-2">
                  <PDKBarcodeIcon className="size-4" />
                  Copy notification Id
                </div>
              </ContextMenuItem>
              <ContextMenuItem
                className="bg-white flex flex-row gap-2 outline-none cursor-pointer hover:bg-gray-100 p-2"
                onSelect={(e) => {
                  e.preventDefault();
                  onDeleteNotification(notification);
                }}
              >
                <PDKMessageSquareXIcon className="size-4" />
                <span>Delete notification</span>
                <span className="ml-auto">
                  <KeyboardKeyIcon>
                    {HOTKEYS.DELETE_SINGLE_NOTIFICATION}
                  </KeyboardKeyIcon>
                </span>
              </ContextMenuItem>
            </ContextMenuContent>
          </ContextMenu>
        </div>
      );
    };

    return (
      <div className="virtualized-inbox-list h-screen">
        <AutoSizer>
          {({ height, width }) => {
            return (
              <FixedSizeList
                className="List"
                height={height}
                // Plus one for the <ClearFindInViewFilter />
                itemCount={
                  numOfFilteredNotifications === 0
                    ? notifications.length
                    : notifications.length + 1
                }
                itemSize={57}
                width={width}
              >
                {Row}
              </FixedSizeList>
            );
          }}
        </AutoSizer>
      </div>
    );
  },
);

export default VirtualizedInboxList;
